// src/components/calculators/VATToggle.js
import React from 'react';
import { 
  Typography, 
  Card,
  CardBody,
  Tooltip
} from "@material-tailwind/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useCalculator } from './CalculatorContext';

const VATToggle = ({ applyVAT, vatRate, onChange }) => {
  const { validateVATRate } = useCalculator();

  const handleVATRateChange = (e) => {
    const value = e.target.value;
    if (validateVATRate(value)) {
      onChange('vatRate', value);
    }
  };

  return (
    <>
      <Card
        className="overflow-hidden"
        style={{ 
          borderRadius: 'var(--radius-m)',
          boxShadow: 'var(--shadow-frame-small)',
          border: `1px solid ${applyVAT ? 'var(--Blue-300)' : 'var(--Stroke-Light)'}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: 'var(--Surface-Primary)'
        }}
      >
        <CardBody style={{ padding: '0' }}>
          {/* Main Toggle Row */}
          <div
            style={{
              padding: 'var(--spacing-l)',
              backgroundColor: applyVAT ? 'var(--Blue-50)' : 'transparent',
              transition: 'background-color 0.3s ease'
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center" style={{ gap: 'var(--spacing-m)' }}>
                {/* Custom Toggle Switch */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={applyVAT}
                  onClick={() => onChange('applyVAT', !applyVAT)}
                  style={{
                    position: 'relative',
                    width: '44px',
                    height: '24px',
                    borderRadius: '9999px',
                    background: applyVAT ? 'var(--brand-gradient-1)' : 'var(--Grey-200)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease',
                    padding: 0,
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: applyVAT ? '22px' : '2px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                      transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: 'block'
                    }}
                  />
                </button>

                <div className="select-none">
                  <Typography
                    style={{ 
                      color: 'var(--Text-Primary)',
                      fontSize: 'var(--font-size-subtitle4)',
                      fontWeight: 'var(--font-weight-subtitle4)',
                      lineHeight: 'var(--line-height-subtitle4)',
                      marginBottom: '2px'
                    }}
                  >
                    Are you VAT registered?
                  </Typography>
                </div>
              </div>

              <Tooltip
                content={
                  <div style={{ maxWidth: '280px', padding: '8px' }}>
                    <Typography
                      style={{ 
                        color: 'white',
                        fontSize: 'var(--font-size-body5)',
                        lineHeight: '1.5'
                      }}
                    >
                      VAT is calculated on the full customer price. Boutee's commission and card fees 
                      are charged on the VAT-inclusive amount. You must remit the full VAT amount to HMRC.
                    </Typography>
                  </div>
                }
                placement="top"
                className="bg-gray-800"
              >
                <button
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '4px',
                    cursor: 'help',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <InformationCircleIcon 
                    className="h-5 w-5" 
                    style={{ 
                      color: 'var(--Grey-500)',
                      transition: 'color 0.2s ease'
                    }} 
                  />
                </button>
              </Tooltip>
            </div>
          </div>

          {/* VAT Rate Input Section */}
          <div
            style={{
              height: applyVAT ? 'auto' : '0',
              opacity: applyVAT ? '1' : '0',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              borderTop: applyVAT ? '1px solid var(--Blue-200)' : 'none'
            }}
          >
            {applyVAT && (
              <div
                style={{
                  padding: 'var(--spacing-m) var(--spacing-l)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: 'white'
                }}
              >
                <div
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 'var(--spacing-s)'
                  }}
                >
                  <Typography
                    style={{ 
                      color: 'var(--Text-Secondary)',
                      fontSize: 'var(--font-size-body4)',
                      fontWeight: 'var(--font-weight-normal)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    VAT Rate:
                  </Typography>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="text"
                      value={vatRate}
                      onChange={handleVATRateChange}
                      style={{
                        width: '48px',
                        height: '32px',
                        border: '1px solid var(--Blue-400)',
                        borderRadius: 'var(--radius-xs)',
                        fontSize: 'var(--font-size-body3)',
                        fontWeight: 'var(--font-weight-medium)',
                        padding: '0 8px',
                        textAlign: 'center',
                        backgroundColor: 'white',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        color: 'var(--Text-Primary)'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--Blue-600)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--Blue-400)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    <Typography
                      style={{ 
                        color: 'var(--Text-Primary)',
                        fontSize: 'var(--font-size-body3)',
                        fontWeight: 'var(--font-weight-medium)'
                      }}
                    >
                      %
                    </Typography>
                  </div>
                </div>
                
                <Typography
                  style={{ 
                    color: 'var(--Text-Tertiary)',
                    fontSize: 'var(--font-size-body5)',
                    fontStyle: 'italic'
                  }}
                >
                  Standard UK rate: 20%
                </Typography>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <style>{`
        button:focus-visible {
          outline: 2px solid var(--Blue-600);
          outline-offset: 2px;
        }
        
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
    </>
  );
};

export default VATToggle;